import { useEffect, useRef, useState } from 'react';
import ReactDatePicker, { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import Popover from '@ui/popover/popover';
import RotatableIcon from '@ui/RotatableIcon/rotatable-icon';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import { formatWatermarkDateTime } from '@/components/watermark/utils/format-watermark-date';
import './watermark-date-picker.css';

// time-related imports — kept for future use
// import { forwardRef } from 'react';
// import { getAdjustedDate, getUserTimezone } from '@ui/calendar/utils';
// import timezoneMap from '@ui/calendar/timezone-short-name-map.json' with { type: 'json' };

// time input component — kept for future use
// interface TimeInputWithIconProps {
//   value?: string;
//   onChange?: (value: string) => void;
//   invalid?: boolean;
// }
// const TimeInputWithIcon = forwardRef<HTMLInputElement, TimeInputWithIconProps>(({ value, onChange, invalid }, ref) => (
//   <div className={`watermark-date-picker__time-input${invalid ? ' watermark-date-picker__time-input--invalid' : ''}`}>
//     <Icon name={ICONS.CLOCK} size={12} color="var(--global-ico-text-1)" />
//     <input ref={ref} type="time" value={value ?? ''} onChange={(e) => onChange?.(e.target.value)} />
//   </div>
// ));

const getDefaultDates = () => {
  // leaving commented out in case this is wrong - the Figmas show "--"
  // and an empty state, but it would make more sense to show the dates here
  // however,  the designs are the designs.

  // const start = new Date();
  // start.setMinutes(start.getMinutes() + 30, 0, 0);
  // const end = new Date(start);
  // end.setMinutes(end.getMinutes() + 30);
  return { start: null };
};

interface WatermarkDatePickerProps {
  value?: string;
  onChange?: (text: string) => void;
}

const WatermarkDatePicker = ({ value, onChange }: WatermarkDatePickerProps) => {
  const [startDate, setStartDate] = useState<Date | null>(() => getDefaultDates().start);
  // const [endDate, setEndDate] = useState<Date | null>(() => getDefaultDates().end);
  // const [selectedTimezone, setSelectedTimezone] = useState<keyof typeof timezoneMap>(
  //   getUserTimezone() as keyof typeof timezoneMap
  // );
  // const [invalidEndTime, setInvalidEndTime] = useState(false);

  const popoverPortalRef = useRef<HTMLDivElement | null>(null);
  // const timezonePortalRef = useRef<HTMLDivElement | null>(null);
  // const sortedTimezones = useMemo(() => Object.entries(timezoneMap).toSorted(([, a], [, b]) => a.localeCompare(b)), []);

  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const emit = (start: Date | null) => {
    if (!start) return;
    onChange?.(formatWatermarkDateTime(start, null, localTimezone));
  };

  useEffect(() => {
    if (!value) emit(startDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateSelect = (date: Date | null) => {
    if (!date) return;
    const newStart = new Date(date);
    setStartDate(newStart);
    emit(newStart);

    // time/end-date logic — kept for future use
    // if (startDate) newStart.setHours(startDate.getHours(), startDate.getMinutes(), 0, 0);
    // const newEnd = endDate ? new Date(date) : null;
    // if (newEnd && endDate) newEnd.setHours(endDate.getHours(), endDate.getMinutes(), 0, 0);
    // setEndDate(newEnd);
    // emit(newStart, newEnd);
  };

  const displayLabel = value || (startDate ? formatWatermarkDateTime(startDate, null, localTimezone) : '--');

  return (
    <Popover
      width="100%"
      popoverWidth="100%"
      portalEl={popoverPortalRef}
      renderTriggerButton={({ open, closing }) => (
        <Button
          border="1px solid var(--global-ico-text-3)"
          hoverBorder="1px solid var(--global-ico-text-4)"
          height="36px"
          width="100%"
          clear
          className="flex ai-center"
          borderRadius="100px"
          disabledOpacity={0.6}
        >
          <Container className="flex flex-1 ai-center jc-space-between gap-6 padding-h-16" minWidth="0">
            <Text className="P2 text-ellipsis ws-nowrap flex-1" design="GlobalIcoText1">
              {displayLabel}
            </Text>
            <RotatableIcon
              name={ICONS.KEYBOARD_ARROW_DOWN}
              size={12}
              color="var(--global-ico-text-1)"
              rotate={open && !closing ? '180deg' : '0'}
            />
          </Container>
        </Button>
      )}
    >
      {({ triggerBounds }) => (
        <Container
          design="GlobalCont3"
          borderRadius="12px"
          className="position-fixed flex-column overflow-hidden"
          style={{
            top: (triggerBounds?.bottom ?? 0) + 4,
            left: (triggerBounds?.right ?? 0) - 240,
            zIndex: 99999,
          }}
        >
          <div className="watermark-date-picker">
            <ReactDatePicker
              selected={startDate}
              onChange={handleDateSelect}
              calendarClassName="watermark-date-picker__calendar"
              renderCustomHeader={({ decreaseMonth, increaseMonth, monthDate }: ReactDatePickerCustomHeaderProps) => (
                <div className="flex fd-row jc-center ai-center width-100-percent height-32 margin-b-8">
                  <Button className="clear-button square-32 flex fd-row jc-center ai-center" onClick={decreaseMonth}>
                    <Icon name={ICONS.KEYBOARD_ARROW_LEFT} size={12} />
                  </Button>
                  <div className="flex-1 S1">
                    <Text design="GlobalIcoText1" className="text-center">
                      {monthDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                    </Text>
                  </div>
                  <Button className="clear-button square-32 flex fd-row jc-center ai-center" onClick={increaseMonth}>
                    <Icon name={ICONS.KEYBOARD_ARROW_RIGHT} size={12} />
                  </Button>
                </div>
              )}
              renderDayContents={(day, date) => {
                const isSelected = startDate && date.toDateString() === startDate.toDateString();
                return (
                  <Container
                    width="100%"
                    height="100%"
                    background={isSelected ? getThemeColorCSSVariable() : 'transparent'}
                    borderRadius="100%"
                    className="flex fd-row jc-center ai-center overflow-hidden"
                  >
                    <Text
                      color={
                        isSelected
                          ? getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)
                          : undefined
                      }
                      className="position-relative"
                    >
                      {day}
                    </Text>
                  </Container>
                );
              }}
              inline
            />
          </div>

          {/* time/timezone section — kept for future use */}
          {/* <Container
            className="margin-16 flex-column gap-8 padding-h-16 padding-v-12"
            design="GlobalCont1"
            borderRadius="8px"
          >
            <Container className="watermark-date-picker__time-row flex ai-center jc-space-between">
              <Text className="S2" color="var(--global-ico-text-1)">
                Start Time
              </Text>
              <ReactDatePicker
                selected={
                  startDate
                    ? new Date(startDate.toLocaleString('en-US', { timeZone: selectedTimezone as string }))
                    : null
                }
                onChange={getAdjustedDate(selectedTimezone as string, (adjusted) => {
                  setStartDate(adjusted);
                  const isValid = !!adjusted && !!endDate && adjusted < endDate;
                  setInvalidEndTime(endDate ? !isValid : false);
                  emit(adjusted, endDate, selectedTimezone as string);
                })}
                disabled={!startDate}
                calendarClassName="watermark-date-picker__time-picker"
                customTimeInput={<TimeInputWithIcon />}
                inline
                showTimeSelectOnly
                timeIntervals={5}
                showTimeInput
              />
            </Container>
            <Container className="watermark-date-picker__time-row flex ai-center jc-space-between">
              <Container className="flex-column">
                <Text className="S2" design="GlobalIcoText1">
                  End Time
                </Text>
                <Text className="C3 margin-t--6" design="GlobalIcoText5">
                  optional
                </Text>
              </Container>
              <ReactDatePicker
                selected={
                  endDate ? new Date(endDate.toLocaleString('en-US', { timeZone: selectedTimezone as string })) : null
                }
                onChange={getAdjustedDate(selectedTimezone as string, (adjusted) => {
                  setEndDate(adjusted);
                  const isValid = !!adjusted && !!startDate && startDate < adjusted;
                  setInvalidEndTime(!isValid);
                  emit(startDate, adjusted, selectedTimezone as string);
                })}
                disabled={!startDate}
                calendarClassName="watermark-date-picker__time-picker"
                customTimeInput={<TimeInputWithIcon invalid={invalidEndTime} />}
                inline
                showTimeSelectOnly
                timeIntervals={5}
                showTimeInput
              />
            </Container>
            <Container background="var(--global-cont-2)" height="1px" />
            <Container className="watermark-date-picker__tz-row">
              <Popover
                closeOnClick
                width="100%"
                popoverWidth="100%"
                portalEl={timezonePortalRef}
                renderTriggerButton={({ open, closing }) => (
                  <Button
                    border="1px solid var(--global-ico-text-3)"
                    hoverBorder="1px solid var(--global-ico-text-4)"
                    height="36px"
                    width="100%"
                    clear
                    className="flex ai-center"
                    borderRadius="100px"
                    disabledOpacity={0.6}
                  >
                    <div className="flex flex-1 ai-center jc-space-between gap-6 padding-h-16">
                      <Text className="P2" color="var(--global-ico-text-1)">
                        {timezoneMap[selectedTimezone] ?? (selectedTimezone as string)}
                      </Text>
                      <RotatableIcon
                        name={ICONS.KEYBOARD_ARROW_DOWN}
                        size={12}
                        color="var(--global-ico-text-1)"
                        rotate={open && !closing ? '180deg' : '0'}
                      />
                    </div>
                  </Button>
                )}
              >
                {({ triggerBounds }) => (
                  <Container
                    background="var(--gray-800)"
                    borderRadius="12px"
                    className="position-fixed flex-column watermark-tz-dropdown"
                    style={{
                      top: (triggerBounds?.bottom ?? 0) + 4,
                      left: triggerBounds?.left ?? 0,
                      width: triggerBounds?.width ?? 208,
                      maxHeight: '240px',
                      overflowY: 'auto',
                      zIndex: 100000,
                    }}
                  >
                    {sortedTimezones.map(([tz, label]) => {
                      const isSelected = tz === selectedTimezone;
                      return (
                        <Button
                          key={tz}
                          width="100%"
                          className="radius-0 text-left flex ai-center"
                          backgroundColor={isSelected ? 'var(--gray-700)' : 'transparent'}
                          hoverBackgroundColor="var(--gray-700)"
                          onClick={() => {
                            setSelectedTimezone(tz as keyof typeof timezoneMap);
                            localStorage.setItem('last-timezone', tz);
                            emit(startDate, endDate, tz);
                          }}
                        >
                          <Text color={isSelected ? 'var(--primary-color)' : 'var(--global-ico-text-1)'} className="S2">
                            {label}
                          </Text>
                        </Button>
                      );
                    })}
                  </Container>
                )}
              </Popover>
            </Container>
          </Container> */}
        </Container>
      )}
    </Popover>
  );
};

export default WatermarkDatePicker;
